import {
  Monitor,
  Play,
  Shield,
  Flag,
  Grid,
  Camera,
  Server,
  Smartphone,
  RotateCcw,
  Lock,
  MessageCircle,
  Gift,
  Diamond,
  CheckCircle,
  Trophy,
} from "lucide-react";
import { useState } from "react";
import apple from '../assets/logo/Apple_Pay.png';
import discover from '../assets/logo/Discover.png';
import cb from '../assets/logo/GIE_CB.png';
import Google from '../assets/logo/Google_Pay.png';
import mastercard from '../assets/logo/Mastercard.png';
import visa from '../assets/logo/Visa.png';
import ideal from '../assets/logo/ideal.svg';
import bancontact from '../assets/logo/Bancontact.png';
import { CONTACT_CONFIG } from "../config/contact";

function getDiscountPercent(original, current) {
  return Math.round(((original - current) / original) * 100);
}

function SeasonDiscountBadge({ originalPrice, currentPrice, variant = "default" }) {
  const pct = getDiscountPercent(originalPrice, currentPrice);
  const isPremium = variant === "premium";
  return (
    <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-black mt-2 ${
      isPremium
        ? "bg-yellow-400 text-black"
        : "bg-gradient-to-r from-yellow-400 to-yellow-600 text-black"
    }`}>
      <Trophy className="w-3 h-3" />
      -{pct}% SEIZOENSAANBIEDING 2026/27
    </div>
  );
}

function SeasonFeature({ variant = "default" }) {
  const isPremium = variant === "premium";
  return (
    <div className={`flex items-start ${isPremium ? "gap-3" : "gap-2 sm:gap-3"}`}>
      <Trophy className={`w-4 h-4 sm:w-5 sm:h-5 mt-1 flex-shrink-0 ${
        isPremium ? "text-yellow-300" : "text-yellow-500 group-hover:text-yellow-300 transition-colors duration-500"
      }`} />
      <span className={`${isPremium ? "text-white text-sm" : "text-gray-700 text-xs sm:text-sm group-hover:text-white transition-colors duration-500"}`}>
        <strong className={isPremium ? "text-yellow-300" : "text-yellow-600 group-hover:text-yellow-300"}>
          Eredivisie &amp; Champions League
        </strong>{" "}
        2026/27 in HD/4K
      </span>
    </div>
  );
}

const FEATURE_TONES = {
  cyan: { def: "text-[#00d4ff] group-hover:text-cyan-200 transition-colors duration-500", prem: "text-[#00d4ff]" },
  blue: { def: "text-blue-600 group-hover:text-blue-300 transition-colors duration-500", prem: "text-blue-300" },
  sky: { def: "text-blue-400 group-hover:text-blue-300 transition-colors duration-500", prem: "text-blue-300" },
  navy: { def: "text-blue-800 group-hover:text-blue-300 transition-colors duration-500", prem: "text-blue-300" },
  orange: { def: "text-orange-500 group-hover:text-orange-300 transition-colors duration-500", prem: "text-orange-300" },
  green: { def: "text-green-500 group-hover:text-green-300 transition-colors duration-500", prem: "text-green-300" },
  pink: { def: "text-pink-500 group-hover:text-pink-300 transition-colors duration-500", prem: "text-pink-300" },
  purple: { def: "text-purple-500 group-hover:text-purple-300 transition-colors duration-500", prem: "text-purple-300" },
};

function FeatureRow({ icon: Icon, tone = "blue", variant, children }) {
  const isPremium = variant === "premium";
  const t = FEATURE_TONES[tone] || FEATURE_TONES.blue;
  return (
    <div className={`flex items-start ${isPremium ? "gap-3" : "gap-2 sm:gap-3"}`}>
      <Icon className={`${isPremium ? "w-5 h-5" : "w-4 h-4 sm:w-5 sm:h-5"} mt-1 flex-shrink-0 ${isPremium ? t.prem : t.def}`} />
      <span className={isPremium ? "text-white text-sm" : "text-gray-700 text-xs sm:text-sm group-hover:text-white transition-colors duration-500"}>
        {children}
      </span>
    </div>
  );
}

function AdultRow({ variant }) {
  const isPremium = variant === "premium";
  return (
    <div className="flex items-start gap-3">
      <div className="relative">
        <Lock
          className={`w-5 h-5 mt-1 flex-shrink-0 ${
            isPremium ? "text-pink-300" : "text-pink-500 group-hover:text-pink-300 transition-colors duration-500"
          }`}
        />
        <div className="absolute -top-2 -right-2 bg-pink-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">
          18
        </div>
      </div>
      <span className={isPremium ? "text-white text-sm" : "text-gray-700 text-xs sm:text-sm group-hover:text-white transition-colors duration-500"}>
        Volwassenenzenders + films (met wachtwoord) 🔞
      </span>
    </div>
  );
}

function PlanFeatures({ variant = "default", adult = false }) {
  const isPremium = variant === "premium";
  return (
    <>
      <FeatureRow icon={Diamond} tone="cyan" variant={variant}>
        <strong className={isPremium ? "text-[#7fe9ff]" : "text-[#090979] group-hover:text-cyan-200"}>
          Officiële Diamond-server
        </strong>{" "}
        voor NL &amp; België
      </FeatureRow>
      <SeasonFeature variant={variant} />
      {adult && <AdultRow variant={variant} />}
      <FeatureRow icon={Monitor} tone="blue" variant={variant}>
        +79.000 zenders, +249.000 films en series
      </FeatureRow>
      <FeatureRow icon={Play} tone="orange" variant={variant}>
        Amazon Prime, Videoland, Viaplay, NPO Start Plus, Discovery+, HBO Max, Netflix, Apple+, Disney+
      </FeatureRow>
      <FeatureRow icon={Shield} tone="green" variant={variant}>
        ESPN Nederland voor de Eredivisie ⚽️
      </FeatureRow>
      <FeatureRow icon={Trophy} tone="purple" variant={variant}>
        Ziggo Sport &amp; Viaplay voor Formule 1
      </FeatureRow>
      <FeatureRow icon={Flag} tone="pink" variant={variant}>
        Onbeperkte keuze aan films en series
      </FeatureRow>
      <FeatureRow icon={Grid} tone="purple" variant={variant}>
        Kwaliteit SD / HD / FULL HD / 4K
      </FeatureRow>
      <FeatureRow icon={Camera} tone="sky" variant={variant}>
        Catch-up &amp; EPG (tv-gids)
      </FeatureRow>
      <FeatureRow icon={Server} tone="navy" variant={variant}>
        Snelle &amp; stabiele servers
      </FeatureRow>
      <FeatureRow icon={Smartphone} tone="green" variant={variant}>
        Compatibel met alle apparaten
      </FeatureRow>
      <FeatureRow icon={RotateCcw} tone="orange" variant={variant}>
        Regelmatig bijgewerkte content
      </FeatureRow>
      <FeatureRow icon={Shield} tone="purple" variant={variant}>
        15 dagen geld-terug-garantie
      </FeatureRow>
    </>
  );
}

const PAYMENT_METHODS = [
  { src: ideal, alt: "iDEAL", h: "h-8 sm:h-9" },
  { src: bancontact, alt: "Bancontact", h: "h-5 sm:h-6" },
  { src: Google, alt: "Google Pay", h: "h-5 sm:h-6" },
  { src: cb, alt: "CB", h: "h-7 sm:h-8" },
  { src: visa, alt: "Visa", h: "h-4 sm:h-5" },
  { src: apple, alt: "Apple Pay", h: "h-5 sm:h-6" },
  { src: discover, alt: "Discover", h: "h-3.5 sm:h-4" },
  { src: mastercard, alt: "Mastercard", h: "h-7 sm:h-8" },
];

function PaymentBanner() {
  return (
    <div className="mt-10 sm:mt-14 max-w-4xl mx-auto">
      <div className="bg-white rounded-xl sm:rounded-2xl border border-gray-100 shadow-lg p-6 sm:p-8 text-center">
        <div className="inline-flex items-center gap-2 text-[#090979] font-bold text-sm sm:text-base">
          <Lock className="w-4 h-4 sm:w-5 sm:h-5" />
          100% Veilig &amp; versleuteld betalen
        </div>
        <p className="text-xs sm:text-sm text-gray-500 mt-1 mb-5 sm:mb-6">
          Betaal eenvoudig met iDEAL, Bancontact of je favoriete kaart
        </p>
        <div className="grid grid-cols-4 md:grid-cols-8 gap-2.5 sm:gap-3">
          {PAYMENT_METHODS.map((p, i) => (
            <div
              key={p.alt}
              style={{ animationDelay: `${i * 80}ms` }}
              className="pay-tile group bg-slate-50 border border-gray-100 rounded-xl h-14 sm:h-16 flex items-center justify-center transition-all duration-300 hover:-translate-y-1 hover:shadow-md hover:bg-white hover:border-[#00d4ff]/40"
            >
              <img
                src={p.src}
                alt={p.alt}
                className={`${p.h} w-auto max-w-[78%] object-contain drop-shadow-sm transition-transform duration-300 group-hover:scale-110`}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function Pricing() {
  const { whatsappUrl: WHATSAPP_URL } = CONTACT_CONFIG;
  const [isMultiScreen, setIsMultiScreen] = useState(false);

  const PRICING_DATA = {
    basic: {
      name: "BASISPAKKET",
      originalPrice: 59.0,
      currentPrice: 49.0,
      duration: "15 MAANDEN",
      bonus: "12 MAANDEN + 3 MAANDEN GRATIS",
      link: "http://maboutiqueonline.store/index_fr.html?id=1",
    },
    premium: {
      name: "PREMIUMPAKKET",
      originalPrice: 69.0,
      currentPrice: 59.0,
      duration: "18 MAANDEN",
      bonus: "18 MAANDEN",
      link: "http://maboutiqueonline.store/index_fr.html?id=2",
    },
    gold: {
      name: "GOUDEN PAKKET",
      originalPrice: 89.0,
      currentPrice: 79.0,
      duration: "18 MAANDEN",
      bonus: "2 SCHERMEN",
      link: "http://maboutiqueonline.store/index_fr.html?id=3",
    },
    multi_2_screens: {
      name: "DIAMANTEN PAKKET",
      originalPrice: 99.0,
      currentPrice: 89.0,
      duration: "2 jaar",
      bonus: "2 SCHERMEN",
      link: "http://maboutiqueonline.store/index_fr.html?id=5",
    },
    multi_3_screens: {
      name: "PLATINUMPAKKET",
      originalPrice: 119.0,
      currentPrice: 99.0,
      duration: "18 MAANDEN",
      bonus: "3 SCHERMEN",
      link: "http://maboutiqueonline.store/index_fr.html?id=6",
    },
    multi_4_screens: {
      name: "LUXEPAKKET",
      originalPrice: 129.0,
      currentPrice: 109.0,
      duration: "18 MAANDEN",
      bonus: "4 SCHERMEN",
      link: "http://maboutiqueonline.store/index_fr.html?id=7",
    },
    lifetime: {
      name: "LIFETIME-PAKKET",
      originalPrice: 279.0,
      price: 189.0,
      features: [
        "Eenmalige betaling",
        "2 schermen",
        "Volledige toegang tot het PREMIUM Plan",
        "30 Dagen Geld-Terug-Garantie"
      ],
      link: "http://maboutiqueonline.store/index_fr.html?id=4"
    }
  };

  const standardPlans = [
    {
      id: "standard-basic",
      cardPosition: "basic",
      name: PRICING_DATA.basic.name,
      badge: PRICING_DATA.basic.bonus,
      originalPrice: PRICING_DATA.basic.originalPrice,
      currentPrice: PRICING_DATA.basic.currentPrice,
      durationLabel: `/${PRICING_DATA.basic.duration}`,
      checkoutPlan: "basic",
      redirectUrl: PRICING_DATA.basic.link,
    },
    {
      id: "standard-premium",
      cardPosition: "premium",
      name: PRICING_DATA.premium.name,
      badge: PRICING_DATA.premium.bonus,
      originalPrice: PRICING_DATA.premium.originalPrice,
      currentPrice: PRICING_DATA.premium.currentPrice,
      durationLabel: `/${PRICING_DATA.premium.duration}`,
      checkoutPlan: "premium",
      redirectUrl: PRICING_DATA.premium.link,
    },
    {
      id: "standard-gold",
      cardPosition: "gold",
      name: PRICING_DATA.gold.name,
      badge: PRICING_DATA.gold.bonus,
      originalPrice: PRICING_DATA.gold.originalPrice,
      currentPrice: PRICING_DATA.gold.currentPrice,
      durationLabel: `/${PRICING_DATA.gold.duration}`,
      checkoutPlan: "gold",
      redirectUrl: PRICING_DATA.gold.link,
    },
  ];

  const multiScreenPlans = [
    {
      id: "DIAMANTEN PAKKET",
      cardPosition: "basic",
      name: PRICING_DATA.multi_2_screens.name,
      badge: `${PRICING_DATA.multi_2_screens.bonus} • ${PRICING_DATA.multi_2_screens.duration.toUpperCase()}`,
      originalPrice: PRICING_DATA.multi_2_screens.originalPrice,
      currentPrice: PRICING_DATA.multi_2_screens.currentPrice,
      durationLabel: `/${PRICING_DATA.multi_2_screens.duration}`,
      checkoutPlan: "multi_2_screens",
      redirectUrl: PRICING_DATA.multi_2_screens.link,
    },
    {
      id: "PLATINUMPAKKET",
      cardPosition: "premium",
      name: PRICING_DATA.multi_3_screens.name,
      badge: `${PRICING_DATA.multi_3_screens.bonus} • ${PRICING_DATA.multi_3_screens.duration.toUpperCase()}`,
      originalPrice: PRICING_DATA.multi_3_screens.originalPrice,
      currentPrice: PRICING_DATA.multi_3_screens.currentPrice,
      durationLabel: `/${PRICING_DATA.multi_3_screens.duration}`,
      checkoutPlan: "multi_3_screens",
      redirectUrl: PRICING_DATA.multi_3_screens.link,
    },
    {
      id: "LUXEPAKKET",
      cardPosition: "gold",
      name: PRICING_DATA.multi_4_screens.name,
      badge: `${PRICING_DATA.multi_4_screens.bonus} • ${PRICING_DATA.multi_4_screens.duration.toUpperCase()}`,
      originalPrice: PRICING_DATA.multi_4_screens.originalPrice,
      currentPrice: PRICING_DATA.multi_4_screens.currentPrice,
      durationLabel: `/${PRICING_DATA.multi_4_screens.duration}`,
      checkoutPlan: "multi_4_screens",
      redirectUrl: PRICING_DATA.multi_4_screens.link,
    },
  ];

  const activePlans = isMultiScreen ? multiScreenPlans : standardPlans;

  const createCheckoutUrl = (plan, redirectUrl) => {
    return `/checkout?plan=${encodeURIComponent(plan)}&redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const formatPrice = (price) => {
    return `€${price}`;
  };

  return (
    <>
      <section className="py-12 sm:py-16 bg-slate-50 animated-bg">
        <div className="main-content w-[90%] sm:w-[90%] md:w-[85%] m-auto">
          {/* Header */}
          <div className="text-center mb-12 sm:mb-16">
            <h1 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl mx-auto font-black mb-3 sm:mb-4 max-w-3xl leading-snug text-center">
              Ontdek Onze{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#04003b] via-[#090979] to-[#00d4ff]">
                Beste Pakketten
              </span>
            </h1>
            <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto">
              Speciale Seizoensaanbieding 2026/27 — tijdelijk verlaagde prijzen
            </p>
          </div>

          {/* Season Promo */}
          <div className="mb-8 sm:mb-10">
            <div className="relative overflow-hidden rounded-xl sm:rounded-2xl bg-gradient-to-r from-[#050B14] via-[#0b1a4f] to-[#090979] border border-yellow-400/30 shadow-xl p-4 sm:p-6">
              <div
                className="absolute inset-0 opacity-30"
                style={{
                  background:
                    "radial-gradient(circle at 12% 8%, rgba(250,204,21,0.28), transparent 42%), radial-gradient(circle at 90% 15%, rgba(37,99,235,0.32), transparent 48%), radial-gradient(circle at 65% 100%, rgba(220,38,38,0.22), transparent 55%)",
                }}
              />
              <div className="absolute inset-0 bg-gradient-to-r from-[#050B14]/90 via-[#050B14]/70 to-[#090979]/80"></div>
              <div className="relative z-10 flex flex-col sm:flex-row items-center justify-between gap-4 text-center sm:text-left">
                <div className="flex flex-col sm:flex-row items-center gap-3 sm:gap-4">
                  <div className="w-12 h-12 rounded-full bg-yellow-400/20 flex items-center justify-center border border-yellow-400/40 flex-shrink-0">
                    <Trophy className="w-6 h-6 text-yellow-400" />
                  </div>
                  <div>
                    <p className="text-yellow-400 text-[10px] sm:text-xs font-bold tracking-[0.2em] uppercase">
                      Eredivisie &amp; Champions League · Seizoen 2026/27
                    </p>
                    <h3 className="text-white text-lg sm:text-xl font-black">
                      Speciale Korting Nieuw Seizoen
                    </h3>
                    <p className="text-gray-300 text-xs sm:text-sm">
                      Alle pakketten bevatten Eredivisie en Champions League in HD/4K
                    </p>
                  </div>
                </div>
                <div className="flex flex-wrap justify-center gap-2">
                  <span className="bg-yellow-400 text-black px-3 py-1.5 rounded-full text-xs font-black">
                    Tot -32%
                  </span>
                  <span className="bg-white/10 text-white border border-white/20 px-3 py-1.5 rounded-full text-xs font-semibold">
                    Live Wedstrijden + Samenvattingen
                  </span>
                  <span className="bg-white/10 text-white border border-white/20 px-3 py-1.5 rounded-full text-xs font-semibold">
                    Directe Activering
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* IBO Promo */}
          <div className="mb-8 sm:mb-12">
            <div className="relative bg-gradient-to-r from-[#00d4ff] to-[#090979] rounded-xl sm:rounded-2xl p-4 sm:p-6 md:p-8 overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-[1.02]">
              <div className="absolute left-0 top-0 w-12 sm:w-16 h-full bg-[#04003b] opacity-80 rounded-r-xl sm:rounded-r-2xl transform -skew-x-12"></div>
              <div className="absolute left-3 sm:left-4 top-0 w-8 sm:w-12 h-full bg-[#030024] opacity-60 rounded-r-xl sm:rounded-r-2xl transform -skew-x-12"></div>

              <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-4 sm:gap-6">
                <div className="flex-1 text-center md:text-left">
                  <h3 className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-black text-white mb-2">
                    Gratis Activering Voor De IBO Pro Player App
                  </h3>
                  <p className="text-blue-100 text-base sm:text-lg font-semibold">
                    Beperkte speciale aanbieding
                  </p>
                </div>

                <div className="flex-shrink-0">
                  <div className="relative group">
                    <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br from-[#00d4ff] to-[#090979] rounded-full shadow-2xl flex items-center justify-center transform group-hover:scale-110 transition-all duration-300">
                      <Gift className="w-8 h-8 sm:w-10 sm:h-10 text-white drop-shadow-lg" />
                    </div>
                    <div className="absolute -top-1 -right-1 w-5 h-5 sm:w-6 sm:h-6 bg-white rounded-full opacity-90 animate-pulse flex items-center justify-center">
                      <Diamond className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-[#00d4ff]" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Toggle Standard / Multi-Screen */}
          <div className="flex justify-center mb-6 sm:mb-8">
            <div className="inline-flex items-center rounded-full bg-white shadow-md p-1 border border-gray-200">
              <button
                type="button"
                onClick={() => setIsMultiScreen(false)}
                className={`px-4 sm:px-6 py-1.5 sm:py-2 text-xs sm:text-sm font-semibold rounded-full transition-all duration-300 ${
                  !isMultiScreen
                    ? "bg-gradient-to-r from-[#04003b] via-[#090979] to-[#00d4ff] text-white shadow"
                    : "text-gray-600 hover:text-gray-900"
                }`}
              >
                STANDAARD
              </button>
              <button
                type="button"
                onClick={() => setIsMultiScreen(true)}
                className={`px-4 sm:px-6 py-1.5 sm:py-2 text-xs sm:text-sm font-semibold rounded-full transition-all duration-300 ${
                  isMultiScreen
                    ? "bg-gradient-to-r from-[#04003b] via-[#090979] to-[#00d4ff] text-white shadow"
                    : "text-gray-600 hover:text-gray-900"
                }`}
              >
                MULTI-SCHERM
              </button>
            </div>
          </div>

          {/* Pricing Cards */}
          <div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8"
            id="pricing"
          >
            {activePlans.map((plan, index) => {
              const isBasic = plan.cardPosition === "basic" && index === 0;
              const isPremium = plan.cardPosition === "premium" && index === 1;
              const isGold = plan.cardPosition === "gold" && index === 2;

              if (isBasic) {
                return (
                  <div
                    key={plan.id}
                    className="pricing-card pricing-card-clean group pricing-toggle-card"
                    id="basic-card"
                  >
                    <div className="text-center mb-6 sm:mb-8 mt-5">
                      <h3 className="text-2xl font-black text-gray-900 mb-3 sm:mb-4 group-hover:text-white transition-colors duration-500">
                        {plan.name}
                      </h3>
                      {plan.badge && (
                        <div className="inline-block bg-gradient-to-r from-[#00d4ff] to-[#090979] text-white px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-sm font-bold mb-4 sm:mb-6">
                          {plan.badge}
                        </div>
                      )}
                      <div className="mb-4 sm:mb-6">
                        {plan.originalPrice && (
                          <span className="text-gray-400 line-through text-lg group-hover:text-blue-200 transition-colors duration-500">
                            {formatPrice(plan.originalPrice)}
                          </span>
                        )}
                        <div className="text-4xl font-black text-red-600 group-hover:text-white transition-colors duration-500">
                          €{plan.currentPrice}
                        </div>
                        {plan.durationLabel && (
                          <span className="text-gray-500 text-sm group-hover:text-blue-200 transition-colors duration-500 mr-3">
                            {plan.durationLabel}
                          </span>
                        )}
                        {plan.originalPrice && (
                          <SeasonDiscountBadge
                            originalPrice={plan.originalPrice}
                            currentPrice={plan.currentPrice}
                          />
                        )}
                      </div>
                    </div>

                    <div className="mb-4 sm:mb-6">
                      <div className="w-full bg-gradient-to-r from-blue-100 to-blue-200 hover:from-blue-200 hover:to-blue-300 text-[#090979] font-bold py-2 px-4 sm:px-6 rounded-lg sm:rounded-xl transition-all duration-300 hover:shadow-lg flex items-center justify-center gap-2 sm:gap-3 border-2 border-blue-300 group-hover:from-blue-200 group-hover:to-blue-300 group-hover:border-blue-400 text-sm sm:text-base">
                        <Gift className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" />
                        <span>+ Gratis IBO Pro Player!</span>
                      </div>
                    </div>

                    <div className="space-y-3 sm:space-y-4 mb-6 sm:mb-8">
                      <PlanFeatures variant="default" />
                    </div>

                    <button
                      className="w-full bg-gradient-to-r from-[#04003b] via-[#090979] to-[#00d4ff] hover:brightness-110 hover:scale-105 text-white font-bold py-3 sm:py-4 px-4 sm:px-6 rounded-xl sm:rounded-2xl transition-all duration-500 hover:shadow-lg group-hover:shadow-2xl text-sm sm:text-base min-h-[48px]"
                      onClick={() => {
                        window.open(
                          createCheckoutUrl(plan.checkoutPlan, plan.redirectUrl),
                          "_blank"
                        );
                      }}
                    >
                      NU BESTELLEN
                    </button>
                  </div>
                );
              }

              if (isPremium) {
                return (
                  <div
                    key={plan.id}
                    className="pricing-card pricing-card-premium pricing-toggle-card"
                  >
                    <div className="badge bg-blue-500 text-white rounded-full px-2 py-1 text-xs font-semibold">
                      MEEST POPULAIR
                    </div>

                    <div className="text-center mb-6 sm:mb-8 mt-5">
                      <h3 className="text-2xl font-black text-white mb-4">
                        {plan.name}
                      </h3>
                      {plan.badge && (
                        <div className="inline-block bg-gradient-to-r from-[#00d4ff] to-[#090979] text-white px-4 py-2 rounded-full text-sm font-bold mb-6">
                          {plan.badge}
                        </div>
                      )}
                      <div className="mb-6">
                        {plan.originalPrice && (
                          <span className="text-blue-200 line-through text-lg">
                            {formatPrice(plan.originalPrice)}
                          </span>
                        )}
                        <div className="text-4xl font-black text-white">
                          €{plan.currentPrice}
                        </div>
                        {plan.durationLabel && (
                          <span className="text-blue-200 text-sm mr-3">
                            {plan.durationLabel}
                          </span>
                        )}
                        {plan.originalPrice && (
                          <SeasonDiscountBadge
                            originalPrice={plan.originalPrice}
                            currentPrice={plan.currentPrice}
                            variant="premium"
                          />
                        )}
                      </div>
                    </div>

                    <div className="mb-6">
                      <div className="w-full bg-gradient-to-r from-blue-200 to-blue-300 hover:from-blue-300 hover:to-blue-400 text-[#090979] font-bold py-2 px-6 rounded-xl transition-all duration-300 hover:shadow-lg flex items-center justify-center gap-3 border-2 border-blue-400">
                        <Gift className="w-5 h-5 text-green-600" />
                        <span>+ Gratis IBO Pro Player!</span>
                      </div>
                    </div>

                    <div className="space-y-4 mb-8">
                      <PlanFeatures variant="premium" adult />
                    </div>

                    <button
                      className="w-full bg-gradient-to-r from-[#04003b] via-[#090979] to-[#00d4ff] hover:brightness-110 hover:scale-105 text-white font-bold py-4 px-6 rounded-2xl transition-all duration-300 hover:shadow-lg shadow-2xl"
                      onClick={() => {
                        window.open(
                          createCheckoutUrl(plan.checkoutPlan, plan.redirectUrl),
                          "_blank"
                        );
                      }}
                    >
                      NU BESTELLEN
                    </button>
                  </div>
                );
              }

              if (isGold) {
                return (
                  <div
                    key={plan.id}
                    className="pricing-card pricing-card-clean group pricing-toggle-card"
                  >
                    <div className="badge bg-blue-500 text-white rounded-full px-2 py-1 text-xs font-semibold">
                      BESTE PRIJS
                    </div>

                    <div className="text-center mb-6 sm:mb-8 mt-5">
                      <h3 className="text-2xl font-black text-gray-900 mb-4 group-hover:text-white transition-colors duration-500">
                        {plan.name}
                      </h3>
                      {plan.badge && (
                        <div className="inline-block bg-gradient-to-r from-[#00d4ff] to-[#090979] text-white px-4 py-2 rounded-full text-sm font-bold mb-6">
                          {plan.badge}
                        </div>
                      )}
                      <div className="mb-6">
                        {plan.originalPrice && (
                          <span className="text-gray-400 line-through text-lg group-hover:text-blue-200 transition-colors duration-500">
                            {formatPrice(plan.originalPrice)}
                          </span>
                        )}
                        <div className="text-4xl font-black text-red-600 group-hover:text-white transition-colors duration-500">
                          €{plan.currentPrice}
                        </div>
                        {plan.durationLabel && (
                          <span className="text-gray-500 text-sm group-hover:text-blue-200 transition-colors duration-500 mr-3">
                            {plan.durationLabel}
                          </span>
                        )}
                        {plan.originalPrice && (
                          <SeasonDiscountBadge
                            originalPrice={plan.originalPrice}
                            currentPrice={plan.currentPrice}
                          />
                        )}
                      </div>
                    </div>

                    <div className="mb-6">
                      <div className="w-full bg-gradient-to-r from-blue-100 to-blue-200 hover:from-blue-200 hover:to-blue-300 text-[#090979] font-bold py-2 px-6 rounded-xl transition-all duration-500 hover:shadow-lg flex items-center justify-center gap-3 border-2 border-blue-300 group-hover:from-blue-200 group-hover:to-blue-300 group-hover:border-blue-400">
                        <Gift className="w-5 h-5 text-green-600" />
                        <span>+ Gratis IBO Pro Player!</span>
                      </div>
                    </div>

                    <div className="space-y-4 mb-8">
                      <PlanFeatures variant="default" adult />
                    </div>

                    <button
                      className="w-full bg-gradient-to-r from-[#04003b] via-[#090979] to-[#00d4ff] hover:brightness-110 hover:scale-105 text-white font-bold py-3 sm:py-4 px-4 sm:px-6 rounded-xl sm:rounded-2xl transition-all duration-500 hover:shadow-lg group-hover:shadow-2xl text-sm sm:text-base min-h-[48px]"
                      onClick={() => {
                        window.open(
                          createCheckoutUrl(plan.checkoutPlan, plan.redirectUrl),
                          "_blank"
                        );
                      }}
                    >
                      NU BESTELLEN
                    </button>
                  </div>
                );
              }

              return null;
            })}
          </div>

          {/* LIFETIME PLAN */}
          <div className="flex justify-center mt-6 sm:mt-8 lg:mt-12">
            <div className="w-full lg:w-1/3 lg:max-w-md">
              <div className="relative bg-gradient-to-b from-[#04003b] to-[#090979] rounded-2xl p-1 shadow-2xl overflow-hidden group transition-all duration-500">
                <div className="relative bg-[#04003b] rounded-xl p-8 h-full flex flex-col items-center text-center">
                  <div className="absolute top-4 right-4">
                    <span className="bg-gradient-to-r from-yellow-400 to-yellow-600 text-black text-xs font-bold px-3 py-1 rounded-full shadow-lg animate-pulse">
                      SEIZOENSAANBIEDING 2026/27
                    </span>
                  </div>

                  <div className="mb-6">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-[#00d4ff] to-[#090979] mb-4 shadow-lg group-hover:scale-110 transition-transform duration-500">
                      <Diamond className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="text-2xl sm:text-3xl font-black text-white mb-2">
                      {PRICING_DATA.lifetime.name}
                    </h3>
                    <div className="flex flex-col items-center gap-1">
                      {PRICING_DATA.lifetime.originalPrice && (
                        <span className="text-blue-300 line-through text-lg">
                          {formatPrice(PRICING_DATA.lifetime.originalPrice)}
                        </span>
                      )}
                      <div className="flex items-baseline justify-center gap-1">
                        <span className="text-4xl sm:text-5xl font-black text-white">
                          €{PRICING_DATA.lifetime.price}
                        </span>
                        <span className="text-blue-200 font-medium">/ eenmalig</span>
                      </div>
                      {PRICING_DATA.lifetime.originalPrice && (
                        <SeasonDiscountBadge
                          originalPrice={PRICING_DATA.lifetime.originalPrice}
                          currentPrice={PRICING_DATA.lifetime.price}
                          variant="premium"
                        />
                      )}
                    </div>
                  </div>

                  <div className="space-y-4 mb-8 w-full text-left">
                    <div className="flex items-center gap-3 bg-[#00d4ff]/10 p-3 rounded-lg border border-[#00d4ff]/30 hover:bg-[#00d4ff]/15 transition-colors">
                      <div className="p-1.5 bg-[#00d4ff]/20 rounded-full">
                        <Diamond className="w-5 h-5 text-[#00d4ff]" />
                      </div>
                      <span className="text-white font-medium">
                        Officiële Diamond-server voor NL &amp; België
                      </span>
                    </div>

                    <div className="flex items-center gap-3 bg-yellow-400/10 p-3 rounded-lg border border-yellow-400/30 hover:bg-yellow-400/15 transition-colors">
                      <div className="p-1.5 bg-yellow-400/20 rounded-full">
                        <Trophy className="w-5 h-5 text-yellow-400" />
                      </div>
                      <span className="text-white font-medium">
                        Eredivisie &amp; Champions League 2026/27 inbegrepen in HD/4K
                      </span>
                    </div>

                    <div className="flex items-center gap-3 bg-white/5 p-3 rounded-lg border border-white/10 hover:bg-white/10 transition-colors">
                      <div className="p-1.5 bg-green-500/20 rounded-full">
                        <CheckCircle className="w-5 h-5 text-green-400" />
                      </div>
                      <span className="text-white font-medium">Eenmalige betaling (Geen abonnement)</span>
                    </div>

                    <div className="flex items-center gap-3 bg-white/5 p-3 rounded-lg border border-white/10 hover:bg-white/10 transition-colors">
                      <div className="p-1.5 bg-blue-500/20 rounded-full">
                        <Monitor className="w-5 h-5 text-blue-400" />
                      </div>
                      <span className="text-white font-medium">2 Gelijktijdige schermen</span>
                    </div>

                    <div className="flex items-center gap-3 bg-white/5 p-3 rounded-lg border border-white/10 hover:bg-white/10 transition-colors">
                      <div className="p-1.5 bg-purple-500/20 rounded-full">
                        <Gift className="w-5 h-5 text-purple-400" />
                      </div>
                      <span className="text-white font-medium">Volledige toegang tot het PREMIUM Plan</span>
                    </div>

                    <div className="flex items-center gap-3 bg-white/5 p-3 rounded-lg border border-white/10 hover:bg-white/10 transition-colors">
                      <div className="p-1.5 bg-orange-500/20 rounded-full">
                        <Shield className="w-5 h-5 text-orange-400" />
                      </div>
                      <span className="text-white font-medium">30 Dagen Geld-Terug-Garantie</span>
                    </div>
                  </div>

                  <button
                    className="w-full bg-gradient-to-r from-[#00d4ff] to-[#090979] hover:from-[#090979] hover:to-[#00d4ff] text-white font-black py-4 px-8 rounded-xl shadow-lg hover:shadow-2xl hover:scale-105 transition-all duration-300 flex items-center justify-center gap-2"
                    onClick={() => {
                      window.open(
                        createCheckoutUrl('lifetime', PRICING_DATA.lifetime.link),
                        "_blank"
                      );
                    }}
                  >
                    <Gift className="w-5 h-5" />
                    NU BESTELLEN
                  </button>

                  <p className="mt-4 text-xs text-blue-200 flex items-center gap-1">
                    <Lock className="w-3 h-3" />
                    100% Veilige Betaling
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Betaalmethoden-banner */}
          <PaymentBanner />

          {/* Contact Section */}
          <div className="mt-12 sm:mt-16 text-center">
            <div className="bg-white rounded-xl sm:rounded-2xl p-6 sm:p-8 shadow-lg border border-gray-100 max-w-4xl mx-auto">
              <div className="flex flex-col md:flex-row items-center justify-between gap-4 sm:gap-6">
                <div className="text-center md:text-left">
                  <p className="text-base sm:text-lg text-gray-700 leading-relaxed">
                    <span className="text-[#00d4ff] font-semibold">
                      Wilt u een abonnement op maat
                    </span>
                    <span className="text-[#090979] font-semibold">
                      {" "}
                      (land, taal, schermen),
                    </span>
                    <br />
                    <span className="text-[#090979] font-semibold">
                      neem dan contact met ons op via WhatsApp of e-mail.
                    </span>
                  </p>
                </div>
                <div className="flex-shrink-0">
                  <a
                    href={WHATSAPP_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 sm:gap-3 bg-[#25d366] hover:bg-[#128C7E] text-white px-4 sm:px-6 py-2.5 sm:py-3 rounded-lg sm:rounded-xl font-semibold transition-all duration-300 hover:shadow-lg hover:scale-105 text-sm sm:text-base min-h-[44px]"
                  >
                    <MessageCircle className="w-4 h-4 sm:w-5 sm:h-5" />
                    WhatsApp
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}